import { Box, Button, Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import UserPackageProductCard from "../../../components/cards/UserPackageProductCard";
import ProductCardSkeleton from "../../../components/skeletons/ProductCardSkeleton";
import { useSnackbar } from "../../../lib/context/snackbarContext";
import { getSimpleProductsByCategoryId } from "../../../services/productsFn";
import VariantProductDialog from "./VariationDialog";
import { t } from "i18next";

const AllProducts = ({
  language,
  categoryId,
  subCategoryId,
  selectedProducts,
  setSelectedProducts,
}) => {
  const dialogRef = useRef(null);
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const limit = 20;

  useEffect(() => {
    if (categoryId) {
      setCurrentPage(1);
      setCategoryProducts([]);
      fetchAllProducts(1, true);
    }
  }, [categoryId, subCategoryId]);

  const fetchAllProducts = async (page = 1, replace = false) => {
    try {
      if (page === 1) setIsLoading(true);
      else setIsLoadingMore(true);

      const response = await getSimpleProductsByCategoryId(
        categoryId,
        subCategoryId,
        page,
        limit
      );

      if (response.data.status === "success") {
        const data = response.data.data;
        setCategoryProducts((prev) =>
          replace ? data.products : [...prev, ...data.products]
        );
        setHasMore(data.products.length >= limit);
      } else {
        showSnackbar(response.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchAllProducts(nextPage);
  };

  const selectedIds = useMemo(
    () => new Set(selectedProducts.map((p) => p._id)),
    [selectedProducts]
  );

  const handleQuickView = (data, isAdded) => {
    dialogRef.current.openDialog({
      product: data,
      isAdded: isAdded,
    });
  };

  return (
    <div>
      <Typography variant="h5" fontWeight={500} textTransform={"capitalize"}>
        {t("allProducts")}
      </Typography>

      {categoryProducts?.length === 0 && (
        <Typography variant="body1" color="textSecondary">
          {t("productNotFound")}.
        </Typography>
      )}

      <Grid container spacing={2} mt={2}>
        {isLoading
          ? Array.from({ length: 8 }).map((_, index) => (
              <Grid key={index} item xs={12} sm={4} md={2}>
                <ProductCardSkeleton />
              </Grid>
            ))
          : categoryProducts?.map((item) => (
              <Grid key={item._id} item xs={6} sm={4} md={2}>
                <UserPackageProductCard
                  language={language}
                  cardData={item}
                  colorName="text.black"
                  handleQuickView={handleQuickView}
                  isAlreadyAdded={selectedIds.has(item._id)}
                />
              </Grid>
            ))}
      </Grid>

      {hasMore && !isLoading && (
        <Box mt={3} textAlign="center">
          <Button
            variant="contained"
            onClick={handleLoadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? t("loading") : t("loadMore")}
          </Button>
        </Box>
      )}

      <VariantProductDialog
        ref={dialogRef}
        selectedProducts={selectedProducts}
        setSelectedProducts={setSelectedProducts}
      />
    </div>
  );
};

export default AllProducts;
